<script setup lang="ts">
import type { FormSubmitEvent } from '#ui/types'

import { RequestFormSchema, type RequestFormSchemaType } from '~~/shared/validation'

definePageMeta({
  colorMode: 'light',
})

useSeoMeta({
  description: 'Mobile Notary and Loan Signing Agent in the Central Valley.',
  title: 'Julia\'s Mobile Notary',
})

const { data: page } = await useAsyncData('index', () => queryContent('/').findOne())

useSchemaOrg([
  defineLocalBusiness({
    '@type': 'ProfessionalService',
    'address': {
      addressCountry: 'United States',
      postalCode: '93274',
    },
    'logo': '/img/julias-mobile-notary-logo.png',
    'name': 'Julia\'s Mobile Notary',
    'openingHoursSpecification': [
      {
        closes: '19:00',
        dayOfWeek: [ 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday' ],
        opens: '08:00',
      },
    ],
    'telephone': '+15596973388',
  }),
])

 import.meta.prerender && defineOgImageComponent('LandingPage', {
  description: 'Notary and Loan Signing Agent in the Central Valley.',
  image: '/img/pink-watercolor-background.png',
  title: 'Julia\'s Mobile Notary',
})

const img = useImage()
const backgroundImageUrl = computed(() => {
  const imgUrl = img('/img/pink-watercolor-background.png', { format: 'webp' })
  return `url('${imgUrl}')`
})

const isSubmitting = ref(false)
const formSubmitted = ref(false)
const formError = ref<string | undefined>(undefined)
const form = ref()
const state = reactive({
  email: undefined,
  location: undefined,
  message: undefined,
  name: undefined,
  phone: undefined,
  time: undefined,
})

async function onSubmit(event: FormSubmitEvent<RequestFormSchemaType>) {
  formError.value = undefined
  try {
    isSubmitting.value = true
    const response = await fetch('/api/request', {
      body: JSON.stringify(event.data),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    })
    if (response.ok) {
      formSubmitted.value = true
    } else {
      formError.value = 'An error occurred while submitting the form. Please try again later.'
      console.error(response)
    }
  } catch (error) {
    formError.value = 'An error occurred while submitting the form. Please try again later.'
    console.error(error)
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <div v-if="page">
    <ULandingHero
      id="hero"
      :description="page.hero.description"
      :links="page.hero.links"
      :ui="{
        title: 'font-custom sm:text-9xl text-7xl font-medium text-balance tracking-normal',
        description: 'font-semibold'
      }"
      :style="`background-image: ${backgroundImageUrl}`"
      class="bg-no-repeat bg-[center_top_-10%] bg-auto sm:bg-contain"
    >
      <template #title>
        {{ page.hero.title }}<UIcon name="i-game-icons-feather" class="ml-2"/>
      </template>
    </ULandingHero>

    <ULandingSection
      :title="page.services.title"
      :description="page.services.description"
      :headline="page.services.headline"
    >
      <UPageGrid id="services" class="scroll-mt-[calc(var(--header-height)+140px+128px+96px)]">
        <ULandingCard
          v-for="(item, index) in page.services.items"
          :key="index"
          v-bind="item"
        />
      </UPageGrid>
    </ULandingSection>

    <ULandingSection id="appointments" class="bg-primary-50 dark:bg-primary-400 dark:bg-opacity-10">
      <ULandingCTA
        v-bind="page.appointments"
        :card="false"
        :ui="{
          body: {
            base: 'gap-10 sm:gap-y-16'
          }
        }"
      >
        <template #default>
          <UButton
            class="m-auto text-xl py-3 px-6"
            icon="i-mdi-cellphone-sound"
            color="primary"
            :label="page.appointments.phone"
            :to="`tel:+1${page.appointments.phone.replace(/\D/g, '')}`"
          />

          <UDivider label="Or" class="w-full md:w-1/2 max-w-96 m-auto"/>

          <UCard class="w-full md:w-1/2 max-w-96 m-auto">
            <UForm
              v-if="!formSubmitted"
              ref="form"
              :schema="RequestFormSchema"
              :state="state"
              class="space-y-6 p-2"
              :validate-on="['submit']"
              @submit="onSubmit"
            >
              <UFormGroup label="Name" name="name">
                <UInput v-model="state.name" icon="i-mdi-user-outline"/>
              </UFormGroup>

              <UFormGroup label="Email" name="email">
                <UInput v-model="state.email" icon="i-mdi-email-outline"/>
              </UFormGroup>

              <UFormGroup label="Phone" name="phone">
                <UInput v-model="state.phone" icon="i-mdi-cellphone"/>
              </UFormGroup>

              <UFormGroup label="Preferred Date & Time" name="time">
                <UInput v-model="state.time" icon="i-mdi-clock-time-four-outline"/>
              </UFormGroup>

              <UFormGroup label="Signing Location" name="location">
                <UInput v-model="state.location" icon="i-mdi-map-marker"/>
              </UFormGroup>

              <UFormGroup label="Request details" name="message">
                <UTextarea
                  v-model="state.message"
                  placeholder="What's the reason for your appointment?"
                  autoresize
                />
              </UFormGroup>

              <div v-if="formError" class="text-red-500 text-sm">
                {{ formError }}
              </div>

              <div class="flex flex-row justify-center sm:justify-start">
                <UButton type="submit" :loading="isSubmitting">
                  Submit Request
                </UButton>
              </div>
            </UForm>
            <div v-else class="text-center space-y-4">
              <UIcon name="i-line-md-phone-call-loop" class="text-5xl text-primary-400"/>
              <h2 class="text-2xl font-semibold">{{ page.appointments.success.title }}</h2>
              <p class="text-lg">{{ page.appointments.success.description }}</p>
            </div>
          </UCard>
        </template>
      </ULandingCTA>
    </ULandingSection>

    <ULandingSection
      id="faq"
      :title="page.faq.title"
      :description="page.faq.description"
      class="scroll-mt-[var(--header-height)]"
    >
      <ULandingFAQ
        multiple
        :items="page.faq.items"
        class="max-w-4xl mx-auto"
      />
    </ULandingSection>

    <ULandingCard
      id="about"
      :title="page.about.title"
      :description="page.about.description"
      color="primary"
      orientation="horizontal"
      class="lg:my-10 lg:mx-auto lg:max-w-screen-lg"
      :ui="{
        background: 'bg-primary-50 dark:bg-primary-400 dark:bg-opacity-10'
      }"
    >
      <NuxtImg
        :src="page.about.image"
        :alt="page.about.imageAlt"
        format="webp"
        class="w-full rounded-full"
        sizes="300px sm:472px"
      /></ULandingCard>
  </div>
  <div v-else>
    <h1>Loading...</h1>
  </div>
</template>
